/**
 * AppShell — layout principal post-login.
 *
 * ┌──────────────────────────────────────────────────────┐
 * │ BarraSuperior (empresa + vista + menú de usuario)    │
 * ├──────────┬───────────────────────────┬───────────────┤
 * │ LeftRail │      ChatWindow           │ (columna de   │
 * │ trabajo  │  o vista de gestión       │  zona Saber)  │
 * └──────────┴───────────────────────────┴───────────────┘
 *
 * R-PT-02: branding data-driven desde descriptor.tenant.branding.
 * R-PT-01: sin lógica de negocio — solo routing y layout.
 */

import { useState, useCallback, useEffect, useMemo } from 'react'
import { TriangleAlert } from 'lucide-react'
import { useSession } from '../context/SessionContext.jsx'
import { deleteChat, getSaber } from '../api/client.js'
import LeftRail   from './LeftRail.jsx'
import ChatWindow from './ChatWindow.jsx'
import ConfirmDeleteChatDialog from './ConfirmDeleteChatDialog.jsx'
// GEN-76 T7: la vista central se resuelve por REGISTRO, no por condicional
// cableado. Importar `vistas.js` es lo que lo puebla (admin, ficheros y la
// entrada única de los mecanismos); dar de alta un mecanismo no toca este
// fichero.
import { vistaDe } from '../mecanismos/registro.js'
import '../vistas.js'
import { RUTA_LIBRO_ESTILO } from '../despacho/LibroEstilo.jsx'
import Rail, { gruposDelRail } from '../despacho/Rail.jsx'
import { seccionDe } from '../lib/carril.js'
import { claveDe, precargarSaber } from '../lib/saberCache.js'
import BarraSuperior from '../despacho/BarraSuperior.jsx'
import CuadroConversacion from '../despacho/CuadroConversacion.jsx'
import ColumnaConversacion from '../despacho/ColumnaConversacion.jsx'
import { useConversacionZona } from '../despacho/useConversacionZona.js'
import { agenteLlamado, sinLlamada } from '../lib/llamada.js'
import { conversacionReciente } from '../lib/conversacionDirecta.js'
import { areaDeHash, ikuDeHash, kbDeHash } from '../lib/citaIku.js'
import { jimmyDe, orquestadoraDe, textoAyuda } from '../lib/cuadro.js'
import tenantConfig from '../tenant/tenant.config.js'

// US-467: mensaje de UI por `code` del envelope de error del kernel para el
// flujo de borrado (propose → approve). Estable frente a cambios de texto del
// backend; sin match, cae al mensaje del error o a un genérico.
const DELETE_ERROR_MESSAGES = {
  forbidden:               'No tienes permiso para eliminar esta conversación.',
  chat_no_encontrado:      'La conversación ya no existe.',
  accion_no_encontrada:    'La acción de borrado ya no está disponible (expiró).',
  accion_no_permitida:     'Esta acción no está permitida.',
  parametros_invalidos:    'No se pudo preparar el borrado.',
  accion_estado_terminal:  'La conversación ya se eliminó o la acción expiró.',
  en_curso:                'El borrado ya está en curso.',
}
function deleteErrorMessage(err) {
  return DELETE_ERROR_MESSAGES[err?.code]
    ?? err?.message
    ?? 'No se pudo eliminar la conversación. Inténtalo de nuevo.'
}

// US-68 (#202): aviso de incompatibilidad de contrato. Se pinta cuando el
// MAJOR de la `contract_version` del kernel no coincide con el que declara el
// front. Degradación visible (no crash silencioso): la app sigue renderizando
// lo que puede, pero avisa al usuario de que algo podría no funcionar.
function ContractBanner({ message }) {
  if (!message) return null
  return (
    <div className="contract-banner" role="alert">
      <span className="contract-banner__icon" aria-hidden="true"><TriangleAlert size={16} strokeWidth={1.8} /></span>
      <span className="contract-banner__text">{message}</span>
    </div>
  )
}


export default function AppShell() {
  const { user, agents, chats, capabilities, logout, refresh, contractWarning, theme, setTheme } = useSession()

  // Chat "borrador": una conversación nueva nace ya con chat_id definitivo
  // (generado en cliente, patrón B4/US-62 — el kernel acepta el id en el
  // primer POST /messages). US-62: Pórtico no almacena memoria, así que el
  // borrador NO se guarda en ninguna caché local; entra en la lista real
  // cuando el kernel lo incluya en `descriptor.chats` del siguiente bootstrap.
  // El chat_id se fija al nacer para que la ventana no se remonte en mitad
  // del streaming.
  // Un borrador puede nacer "dirigido" a un agente concreto (US-02, al pinchar
  // un agente del rail) o sin dirigir (US-03, el kernel enruta a Anne). El
  // `agente_destino` (id) y `agente_nombre` viajan en la metadata del chat para
  // reflejar el interlocutor en cabecera y mandarlo en cada POST.
  const makeDraft = (agent = null, directa = false) => ({
    chat_id: `c-${crypto.randomUUID()}`,
    titulo: agent ? `Conversación con ${agent.nombre}` : 'Nueva conversación',
    de_que_va: '',
    agentes_en_chat: [
      agent
        ? { nombre: agent.nombre, proposito: 'consulta directa' }
        : { nombre: 'Anne', proposito: 'orquestación' },
    ],
    agente_destino: agent?.id ?? null,
    agente_nombre: agent?.nombre ?? null,
    // GEN-PX-011: conversación directa, sin asunto -> la cabecera muestra solo al agente.
    directa: !!agent && directa,
    updated_at: new Date().toISOString(),
  })

  const [draft, setDraft]                   = useState(makeDraft)
  const [activeChatId, setActiveChatId]     = useState(null)
  // GEN-PX-007: la columna izquierda (histórico de chats) se muestra por defecto
  // en todas partes menos en Inicio; el usuario la abre/cierra con el botón Chats
  // del raíl o el de la barra superior. `null` = lo que toque por defecto.
  const [leftOverride, setLeftOverride]     = useState(null)
  // Vista del área central: 'chat' (por defecto) o una sección del menú
  // (data-driven, capabilities.menu). US-26 añade la vista 'admin'.
  const [activeView, setActiveView]         = useState('chat')
  // GEN-PX-023: entrada hija elegida en el carril contextual del botón activo (null = la primera).
  const [activeSub, setActiveSub]           = useState(null)
  // GEN-PX-006: perfil de otro empleado (la fila que ya tiene la entrada). null = el perfil propio.
  const [perfilEmpleado, setPerfilEmpleado] = useState(null)
  // RESPALDO TEMPORAL (kernel anterior a GEN-PX-022, que no emite el ítem `saber`): vale la regla anterior, GET /v1/ekb/saber → visible.
  // Se quita con el kernel de PX-022 desplegado, junto con la retirada del panel derecho (GEN-PX-021).
  const saberEnMenu = !!capabilities?.menu?.some(m => m.id === 'saber')
  const [saberVisible, setSaberVisible] = useState(false)
  useEffect(() => {
    if (saberEnMenu) return undefined
    let vivo = true
    getSaber().then(r => { if (vivo) setSaberVisible(!!r?.visible) }).catch(() => { if (vivo) setSaberVisible(false) })
    return () => { vivo = false }
  }, [user?.rol, saberEnMenu])
  // GEN-PX-033: al entrar (sesión iniciada) se precarga en segundo plano lo que Saber pinta; abrir Saber es entonces instantáneo.
  const claveSaber = claveDe(user)
  useEffect(() => { precargarSaber(claveSaber) }, [claveSaber])
  // GEN-PX-001: página oculta del libro de estilo. No está en el menú: solo se
  // abre con la ruta #/libro-de-estilo y solo para admin.
  useEffect(() => {
    const abrirSiRuta = () => {
      if (window.location.hash === RUTA_LIBRO_ESTILO && user?.rol === 'admin') setActiveView('libro-de-estilo')
      // GEN-EKB-023: cita de un iKu (#/saber/iku/<id>) → Saber con ese iKu en el centro. El hash se limpia
      // para que pulsar de nuevo la misma cita vuelva a disparar `hashchange`.
      const ikuId = ikuDeHash(window.location.hash)
      if (ikuId) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search)
        setIkuPedido({ id: ikuId, n: Date.now() })
        setActiveView('saber')
      }
      // GEN-PX-031: página de un área (#/saber/area/<id>) → Saber; el hash se conserva y SaberPanel lo lee.
      if (areaDeHash(window.location.hash)) setActiveView('saber')
      // GEN-EKB-024: enlace a un KB (#/saber/kb/<id>), el que da Jimmy al crear uno → Saber con su lista de iKus.
      const kbId = kbDeHash(window.location.hash)
      if (kbId) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search)
        setIkuPedido({ kbId, n: Date.now() })
        setActiveView('saber')
      }
    }
    abrirSiRuta()
    window.addEventListener('hashchange', abrirSiRuta)
    return () => window.removeEventListener('hashchange', abrirSiRuta)
  }, [user?.rol])
  // Fuentes citadas del hilo activo (RightRail «Fuentes citadas», #280) —
  // se derivan en ChatWindow de los `answer` recibidos; aquí solo se
  // reenvían al rail de contexto. Vacío por defecto: no se inventa el dato.
  const [chatSources, setChatSources]       = useState([])
  const [chatAgentes, setChatAgentes]       = useState([])  // GEN-195: ids que han respondido
  // GEN-HU-033: «+ Añadir agente» pide al composer del chat que empiece por
  // «@Nombre ». ChatWindow lo consume (y lo limpia) para no repetirlo al remontar.
  const [mencionPedida, setMencionPedida]   = useState(null)
  // GEN-PX-004: mensaje escrito en el cuadro fijo desde Inicio o una zona sin
  // conversación. Lo recoge la ChatWindow del borrador nuevo, que lo envía.
  const [envioInicial, setEnvioInicial]     = useState(null)
  // GEN-EKB-022: frase de una sugerencia de Saber; se escribe en el cuadro central sin enviarla.
  const [textoPedido, setTextoPedido]       = useState(null)
  // GEN-EKB-022b: cada vez que Jimmy cierra un turno, Saber vuelve a pedir su resumen al kernel.
  const [saberVersion, setSaberVersion]     = useState(0)
  // GEN-EKB-023: iKu que una cita de Jimmy pide abrir en Saber ({ id, n }); `n` distingue pulsaciones repetidas.
  const [ikuPedido, setIkuPedido]           = useState(null)

  const effectiveChatId = activeChatId ?? draft.chat_id
  const isDraft = !chats.some(c => c.chat_id === effectiveChatId)
  const activeChat = chats.find(c => c.chat_id === effectiveChatId)
    ?? (draft.chat_id === effectiveChatId ? draft : null)

  // Inicio = borrador sin dirigir y sin enviar (lo que ChatWindow pinta como entrada).
  const enInicio = activeView === 'chat' && isDraft && !draft.iniciado
    && !draft.agente_destino && !envioInicial
  const leftVisible = leftOverride ?? !enInicio
  const leftCollapsed = !leftVisible

  // Etiqueta de la vista actual para la TopBar (Gabinete: la marca vive en el
  // rail; la barra muestra dónde estás). Data-driven por capabilities.menu.
  const menuItem = capabilities?.menu?.find(m => m.id === activeView)
  const viewLabel = activeView === 'chat'
    ? (isDraft ? 'Inicio' : (activeChat?.titulo ?? 'Conversación'))
    : activeView === 'saber' ? 'Saber' : activeView === 'perfil' ? 'Perfil' : activeView === 'personas' ? 'Personas' : activeView === 'agentes' ? 'Agentes' : activeView === 'normas' ? 'Normas' : activeView === 'admin' ? 'Admin' : activeView === 'libro-de-estilo' ? 'Marca y estilo' : (menuItem?.label ?? 'Inicio')

  // GEN-PX-023: sección del carril contextual (el botón activo y sus hijos, que decide el kernel); null si el botón no tiene hijos.
  // Los ids que ya tienen botón en el raíl nunca se leen como hijos de otro.
  const menuKernel = capabilities?.menu
  const seccion = useMemo(() => {
    const botones = gruposDelRail(menuKernel, saberVisible).flat().map(b => b.destino)
    return seccionDe(menuKernel, activeView, activeSub, botones)
  }, [menuKernel, saberVisible, activeView, activeSub])

  // Seleccionar un chat vuelve siempre a la vista de conversación.
  const handleSelectChat = useCallback((chatId) => {
    setActiveChatId(chatId)
    setActiveView('chat')
  }, [])

  const handleNewChat = useCallback(() => {
    setDraft(makeDraft())
    setActiveChatId(null)
    setActiveView('chat')
    setLeftOverride(null)   // Inicio: la columna vuelve a su estado por defecto (plegada)
  }, [])

  const handleSelectAgent = useCallback((agent, directa = false) => {
    // US-02: abrir un chat nuevo ya dirigido a ese agente. El destino viaja en
    // la metadata del borrador -> ChatWindow lo usa en cabecera y en el POST.
    // GEN-PX-011: `directa` (solo desde la tarjeta de Inicio) = sin asunto, cabecera solo con el agente.
    setDraft(makeDraft(agent, directa === true))
    setActiveChatId(null)
    setActiveView('chat')
  }, [])

  // GEN-BUG-009: «Nueva conversación» del cuadro = borrador vacío con el agente de la conversación cargada (nunca Inicio ni
  // la conversación anterior: `handleSelectAgent` no retoma). Sin agente, el comportamiento de siempre (Inicio).
  const handleNuevaDelCuadro = useCallback((agent) => (agent ? handleSelectAgent(agent, true) : handleNewChat()), [handleSelectAgent, handleNewChat])

  // GEN-PX-011: pulsar la tarjeta de un agente en Inicio. Retoma la conversación
  // con él si su última actividad es de menos de 24 h; si no, abre una nueva dirigida
  // a él (lo de siempre, `handleSelectAgent`). Criterio en lib/conversacionDirecta.js.
  const [retomada, setRetomada] = useState(null)  // { chatId, agente }
  const handleAbrirAgente = useCallback((agent) => {
    const chatId = conversacionReciente(agent, chats)
    if (!chatId) return handleSelectAgent(agent, true)
    setRetomada({ chatId, agente: agent })
    handleSelectChat(chatId)
  }, [chats, handleSelectAgent, handleSelectChat])

  // GEN-PX-004: abre una conversación nueva con el mensaje ya enviado. Sin agente,
  // con Anne (el kernel enruta, como desde Inicio); con agente (elegido con @), dirigida a él.
  const abrirConversacion = useCallback((texto, agente = null) => {
    setDraft(makeDraft(agente))
    setActiveChatId(null)
    setActiveView('chat')
    setEnvioInicial(texto ? { texto } : null)
  }, [])

  // GEN-EKB-022: en Saber el destinatario por defecto es Jimmy (si está operativo); una @ gana.
  const jimmy = jimmyDe(agents)
  const enSaber = activeView === 'saber'
  // GEN-EKB-022b: en Saber la conversación con Jimmy va en la columna derecha, sin navegar.
  const zona = useConversacionZona({ zona: 'saber', agente: jimmy, usuario: user?.id ?? user?.email ?? null, activa: enSaber, chats })
  const enviarDesdeZona = useCallback((texto) => {
    if (enSaber && jimmy) { zona.enviar(texto); return }
    const agente = agenteLlamado(texto, agents)
    if (agente) abrirConversacion(sinLlamada(texto), agente)
    else abrirConversacion(texto, null)
  }, [agents, abrirConversacion, enSaber, jimmy, zona.enviar])  // eslint-disable-line react-hooks/exhaustive-deps

  // Sugerencia de Saber: escribe la frase en el cuadro central, sin enviarla, y abre la columna.
  const pedirAJimmy = useCallback((texto) => {
    zona.abrir()
    setTextoPedido({ texto, n: Date.now() })
  }, [zona.abrir])  // eslint-disable-line react-hooks/exhaustive-deps

  // Navegar a una sección del menú (data-driven). La vista se resuelve en el
  // registro por id de ítem; sin vista registrada se cae a la conversación.
  const handleNavigate = useCallback((viewId, subId = null) => {
    // GEN-PX-007: «Chats» del raíl abre el histórico (columna izquierda con buscador);
    // pulsarlo estando ya en la vista de chat lo alterna.
    if (viewId === 'chat') setLeftOverride(activeView === 'chat' ? !leftVisible : true)
    // Si había una ruta oculta en el hash, se limpia para que volver a pulsar
    // el acceso a ella dispare de nuevo `hashchange`.
    if (window.location.hash === RUTA_LIBRO_ESTILO) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
    }
    // GEN-PX-023: un botón con hijos enseña su carril aunque estuviera plegado.
    if (leftOverride === false && seccionDe(menuKernel, viewId, subId, gruposDelRail(menuKernel, saberVisible).flat().map(b => b.destino))) setLeftOverride(true)
    setActiveSub(subId ?? (viewId === activeView ? activeSub : null))  // pulsar el botón en el que ya estás no te saca de tu entrada
    setActiveView(viewId)
  }, [activeView, activeSub, leftVisible, leftOverride, menuKernel, saberVisible])

  // GEN-PX-006: «Perfil» del avatar -> el propio; una píldora de «Tus empleados» -> el de ese empleado.
  const abrirPerfil = useCallback((empleado = null) => {
    setPerfilEmpleado(empleado)
    handleNavigate('perfil')
  }, [handleNavigate])

  const VistaActiva = vistaDe(activeView)

  // GEN-PX-010/022: «Ver todo el equipo» lleva a Agentes, solo si el menú del kernel emite `agentes` (a todos los perfiles).
  const agentesItem = capabilities?.menu?.find(m => m.id === 'agentes' && m.visible !== false && m.enabled !== false)
  const irAEquipo = agentesItem ? () => handleNavigate('agentes') : undefined
  // GEN-PX-012/022: «Invitar» lleva a Personas y solo existe si el menú del kernel da Administración (la gestión sigue siendo del admin).
  const adminItem = capabilities?.menu?.find(m => m.id === 'admin' && m.visible !== false && m.enabled !== false)
  const irAInvitar = adminItem ? () => handleNavigate('personas') : undefined
  // GEN-PX-022: el «Ver todo el equipo» de «Tus compañeros» lleva a Personas (misma condición que su entrada del Rail).
  const personasItem = capabilities?.menu?.find(m => m.id === 'personas' && m.visible !== false && m.enabled !== false)
  const irAPersonas = personasItem || adminItem ? () => handleNavigate('personas') : undefined

  // Primer mensaje del borrador: refresca en memoria el título del borrador
  // (derivado del mensaje) para que la cabecera lo refleje durante la sesión.
  // US-62: no se persiste nada — la conversación aparecerá en la lista real
  // cuando el kernel la incluya en `descriptor.chats` del siguiente bootstrap.
  // GEN-PT-004: al cerrarse un turno, la lista real (descriptor.chats) se relee
  // del kernel. Silencioso: si falla, se conserva la lista que había.
  const handleTurnDone = useCallback(() => {
    refresh().catch(err => console.error('refresh de la lista de chats:', err))
  }, [refresh])

  const handleFirstSend = useCallback((content) => {
    const titulo = content.length > 48 ? `${content.slice(0, 48)}…` : content
    setDraft(d => ({ ...d, titulo, iniciado: true }))
  }, [])

  // US-467 — borrar una conversación del historial. El rail pide borrar un chat;
  // se abre el diálogo de confirmación (evita borrados accidentales). Al
  // confirmar, se ejecuta el flujo gobernado (propose → approve, borrado real en
  // el store) y se refresca la lista DESDE EL STORE (re-bootstrap), no en local.
  const [chatToDelete, setChatToDelete] = useState(null)  // chat pendiente de confirmar
  const [deleting, setDeleting]         = useState(false)  // borrado en vuelo
  const [deleteError, setDeleteError]   = useState(null)   // error del último intento

  const handleRequestDeleteChat = useCallback((chat) => {
    setDeleteError(null)
    setChatToDelete(chat)
  }, [])

  const handleCancelDelete = useCallback(() => {
    if (deleting) return          // no cancelar a mitad de un borrado en curso
    setChatToDelete(null)
    setDeleteError(null)
  }, [deleting])

  const handleConfirmDelete = useCallback(async () => {
    const chat = chatToDelete
    if (!chat) return
    setDeleting(true)
    setDeleteError(null)
    try {
      // Flujo gobernado de dos pasos (contrato US-467): propone `purgar_chat` y
      // aprueba; el borrado real lo hace el chat store R0 (no la caché local).
      await deleteChat(chat.chat_id)
      // Refrescar la lista DESDE EL STORE (US-62/US-65): el descriptor
      // rehidratado ya no incluye el chat borrado; no se edita solo en local.
      await refresh()
      // Si el chat borrado era el activo, volver a un borrador nuevo (su ventana
      // ya no tiene historial que mostrar).
      if (activeChatId === chat.chat_id) {
        setDraft(makeDraft())
        setActiveChatId(null)
        setActiveView('chat')
      }
      setChatToDelete(null)   // cerrar el diálogo
    } catch (err) {
      setDeleteError(deleteErrorMessage(err))
    } finally {
      setDeleting(false)
    }
  }, [chatToDelete, refresh, activeChatId])

  return (
    <div className="app-marco">
    {/* GEN-PX-002: raíl Despacho. Se añade al marco; los paneles actuales siguen. */}
    <Rail
      tenant={tenantConfig}
      menu={capabilities?.menu}
      saberVisible={saberVisible}
      activeView={activeView}
      onNavigate={handleNavigate}
      onHome={handleNewChat}
    />
    <div className="app-shell">
      <BarraSuperior
        empresa={tenantConfig.nombre}
        user={user}
        viewLabel={viewLabel}
        theme={theme}
        setTheme={setTheme}
        onLogout={logout}
        onPerfil={() => abrirPerfil(null)}
        onToggleLeft={() => setLeftOverride(!leftVisible)}
      />

      <ContractBanner message={contractWarning} />

      <div className="app-body">
        <LeftRail
          collapsed={leftCollapsed}
          soloHistorico={enInicio}
          activeChatId={activeView === 'chat' ? effectiveChatId : null}
          activeView={activeView}
          activeChat={activeChat}
          sources={chatSources}
          agentesQueRespondieron={chatAgentes}
          onSelectChat={handleSelectChat}
          onNewChat={handleNewChat}
          onSelectAgent={handleSelectAgent}
          onAnadirAgente={agent => { setActiveView('chat'); setMencionPedida({ nombre: agent.nombre, n: Date.now() }) }}
          onNavigate={handleNavigate}
          onDeleteChat={handleRequestDeleteChat}
          seccion={seccion}
          onElegirHijo={h => handleNavigate(seccion.padre.id, h.id)}
        />

        <main className="main-area">
          {VistaActiva ? (
            <>
              {/* GEN-PX-004: el contenido de la zona se desplaza por detrás del cuadro fijo. */}
              <div className="dk-zona">
                <VistaActiva viewId={activeView} subId={seccion?.activo.id ?? null} empleado={perfilEmpleado} onNavegar={handleNavigate} onVerPerfil={abrirPerfil} onSelectAgent={handleSelectAgent} onAbrirAgente={handleAbrirAgente} onPedirAJimmy={pedirAJimmy} onAbrirConversacion={abrirConversacion} refrescoSaber={saberVersion} ikuPedido={ikuPedido} />
              </div>
              <CuadroConversacion
                agents={agents}
                destinatario={(enSaber && jimmy) || orquestadoraDe(agents) || { id: 'anne', nombre: 'Anne' }}
                modoSaber={enSaber && !!jimmy}
                placeholder={textoAyuda({
                  zona: enSaber && jimmy ? 'saber' : activeView === 'ficheros' ? 'ficheros' : 'otra',
                  enrutadora: orquestadoraDe(agents)?.nombre ?? 'Anne',
                  agente: jimmy?.nombre,
                })}
                onEnviar={enviarDesdeZona}
                textoPedido={textoPedido}
                onTextoConsumido={() => setTextoPedido(null)}
                deshabilitado={zona.ocupada}
                onNuevaConversacion={enSaber && jimmy ? zona.nueva : undefined}
                nuevaDeshabilitada={!zona.puedeNueva}
              />
            </>
          ) : (
            <ChatWindow
              key={effectiveChatId}
              chatId={effectiveChatId}
              chatMeta={activeChat}
              isDraft={isDraft}
              onFirstSend={handleFirstSend}
              onTurnDone={handleTurnDone}
              onSourcesChange={setChatSources}
              onAgentesChange={setChatAgentes}
              onSelectChat={handleSelectChat}
              onSelectAgent={handleSelectAgent}
              onAbrirAgente={handleAbrirAgente}
              agenteRetomado={retomada?.chatId === effectiveChatId ? retomada.agente : null}
              onVerEquipo={irAEquipo}
              onVerPersonas={irAPersonas}
              onInvitar={irAInvitar}
              onVerPerfil={abrirPerfil}
              mencionPedida={mencionPedida}
              onMencionConsumida={() => setMencionPedida(null)}
              envioInicial={envioInicial}
              onEnvioConsumido={() => setEnvioInicial(null)}
              onAbrirConversacion={abrirConversacion}
              onNuevaConversacion={handleNuevaDelCuadro}
            />
          )}
        </main>

        {/* GEN-PX-021: sin panel derecho. Solo queda la columna de conversación de zona (Saber), cuando está abierta. */}
        {zona.visible && (
          <ColumnaConversacion
            agente={jimmy}
            chat={zona.chat}
            esBorrador={zona.esBorrador}
            envio={zona.envio}
            ocupada={zona.ocupada}
            onAbrirEnChats={() => handleSelectChat(zona.chat.chat_id)}
            onCerrar={zona.cerrar}
            onPrimerEnvio={zona.alPrimerEnvio}
            onTurnoCerrado={() => { handleTurnDone(); setSaberVersion(v => v + 1) }}
            onOcupado={zona.setOcupada}
            onEnvioConsumido={zona.consumirEnvio}
          />
        )}
      </div>

      {/* US-467: confirmación de borrado. Se monta solo cuando hay un chat
          pendiente; captura la aprobación humana explícita del irreversible. */}
      {chatToDelete && (
        <ConfirmDeleteChatDialog
          chat={chatToDelete}
          busy={deleting}
          error={deleteError}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}
    </div>
    </div>
  )
}
